import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { motion } from "framer-motion";
import { BASE_URL } from "../../../config/clientConfig";

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#4361EE", "#7C3AED", "#0EA5E9", "#10B981",
  "#F59E0B", "#EF4444", "#EC4899", "#14B8A6",
];

const getInitials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

const getAvatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Stars = ({ count }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: Math.max(0, Math.min(5, count)) }).map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const Card = ({ t }) => (
  <div className="bg-white border border-surface-border rounded-2xl p-6 flex flex-col gap-4 shadow-sm h-full min-w-[300px] max-w-[340px] flex-shrink-0 mx-3">
    <Stars count={t.rating} />
    <p className="text-sm text-brand-muted leading-relaxed flex-1">"{t.content}"</p>
    <div className="flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
        style={{ background: getAvatarColor(t.full_name) }}
      >
        {getInitials(t.full_name)}
      </div>
      <div>
        <p className="text-sm font-semibold text-brand-text">{t.full_name}</p>
        <p className="text-xs text-brand-light">
          {[t.job_title, t.company].filter(Boolean).join(", ")}
        </p>
      </div>
    </div>
  </div>
);

// ── Section ───────────────────────────────────────────────────────────────────
const TestimonialsSection = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE_URL}/api/v1/testimonials/public`)
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.testimonials;
        if (Array.isArray(list) && list.length > 0) setItems(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const useMarquee = items.length > 10;
  // For marquee: duplicate once for seamless loop
  const marqueeItems = [...items, ...items];

  return (
    <section id="testimonials" className="py-24 overflow-hidden" style={{ background: "#F1F5F9" }}>
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-block mb-3 text-xs font-bold tracking-widest text-primary uppercase bg-primary/8 rounded-full px-4 py-1.5 border border-primary/20">
            Testimonials
          </span>
          <h2 className="text-3xl font-extrabold text-brand-text mb-3">
            Trusted by CISOs
          </h2>
          <p className="text-brand-muted">
            Join 500+ compliance teams who use AIPolicyScan daily.
          </p>
        </motion.div>

        {/* ── Static grid (≤ 10 items) ── */}
        {!useMarquee && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((t, i) => <Card key={t.id ?? i} t={t} />)}
          </div>
        )}
      </div>

      {/* ── Marquee (> 10 items) ── */}
      {useMarquee && (
        <div className="relative">
          <div className="pointer-events-none absolute left-0 top-0 h-full w-24 z-10" style={{ background: "linear-gradient(to right, #F1F5F9, transparent)" }} />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-24 z-10" style={{ background: "linear-gradient(to left, #F1F5F9, transparent)" }} />

          {/* Row 1 — left to right */}
          <div className="flex overflow-hidden mb-4 group">
            <motion.div
              className="flex group-hover:[animation-play-state:paused]"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            >
              {marqueeItems.map((t, i) => <Card key={i} t={t} />)}
            </motion.div>
          </div>

          {/* Row 2 — right to left */}
          <div className="flex overflow-hidden group">
            <motion.div
              className="flex group-hover:[animation-play-state:paused]"
              animate={{ x: ["-50%", "0%"] }}
              transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            >
              {[...marqueeItems].reverse().map((t, i) => <Card key={i} t={t} />)}
            </motion.div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TestimonialsSection;
